import { type ReactNode, useEffect, useState } from "react";
import { Button, type ButtonProps } from "@/components/ui/button";

const CONFIRM_WINDOW_MS = 3000;

/**
 * Two-step destructive button: the first click arms it, the second (within a
 * few seconds) fires. Avoids modal dialogs on the canvas.
 */
export function ConfirmButton({
	onConfirm,
	children,
	confirmLabel = "Confirm?",
	...props
}: Omit<ButtonProps, "onClick"> & {
	confirmLabel?: ReactNode;
	onConfirm: () => void;
}) {
	const [armed, setArmed] = useState(false);

	useEffect(() => {
		if (!armed) {
			return;
		}
		const timer = setTimeout(() => setArmed(false), CONFIRM_WINDOW_MS);
		return () => clearTimeout(timer);
	}, [armed]);

	return (
		<Button
			{...props}
			onClick={() => {
				if (armed) {
					setArmed(false);
					onConfirm();
				} else {
					setArmed(true);
				}
			}}
			variant={armed ? "destructive" : props.variant}
		>
			{armed ? confirmLabel : children}
		</Button>
	);
}
